import React, { useState } from "react";
import {
  Dropdown,
  Popover,
  Tooltip,
  Badge,
  Button,
  Modal,
  notification,
} from "antd";
import {
  EditOutlined,
  DownloadOutlined,
  HistoryOutlined,
  MoreOutlined,
  FileTextOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { handleDownload } from "views/pages/Invoice/utils";
import environment from "environment";
import { translateStatus } from "utilities/TranslationUtility";
import StatusBadge from "views/components/common/statusbadge/StatusBadge";
import { markDocumentOpened } from "../services/openedTracker";
import { saveQueueScrollTop } from "../services/queueScroll";
import NotesModal from "views/components/common/notes/NotesModal";
import { isFailedStatus } from "../components/FailureDrawer";
import documentService from "../services/documentService";
import { useDispatch } from "react-redux";
import CommonAction from "stores/common/CommonAction";

/**
 * Row action menu (roast R08/R25). Replaces the four tiny <img onClick>
 * icons with a labelled Dropdown: larger hit targets, keyboard focusable,
 * text labels instead of icon-guessing, and a notes entry that shows the
 * thread size as a badge. Failed documents open the triage drawer.
 */

const Actions = ({
  record,
  selectedHistory,
  handleSelectedHistory,
  onOpenFailure,
  onDataChanged,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [notesOpen, setNotesOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [currentNotesLog, setCurrentNotesLog] = useState(record.notes_log || []);
  const [currentLegacyNote, setCurrentLegacyNote] = useState(record.notes);

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    const options = {
      weekday: "short",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Paris",
    };
    const formatter = new Intl.DateTimeFormat(t("currentLanguage"), options);
    const capitalize = (str) =>
      str
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    return capitalize(formatter.format(date).replace(/,/g, ""));
  };

  const notesLog = Array.isArray(currentNotesLog) ? currentNotesLog : [];
  const notesCount = notesLog.length || (currentLegacyNote?.trim() ? 1 : 0);
  const latestNote = notesLog.length
    ? notesLog[notesLog.length - 1]
    : currentLegacyNote
    ? { text: currentLegacyNote, author_name: "", created_at: null }
    : null;

  const handleNotesSaved = (text, updatedLog) => {
    setCurrentNotesLog(updatedLog);
    setCurrentLegacyNote(text);
  };

  const HistoryPopup = ({ data }) => {
    const sortedData = [...(data || [])].sort((a, b) =>
      String(b.modified_at_CET || "").localeCompare(String(a.modified_at_CET || ""))
    );
    return (
      <div className="vh-popover history-popover">
        <div className="history-header pb-0 px-3">
          <h1 className="dark-text fs14 mb-2 poppins-medium">
            {t("versionHistory")}
          </h1>
        </div>
        {sortedData.length === 0 ? (
          <div className="popup-item px-3 pt-1 pb-2 fs12 cell-gray">—</div>
        ) : (
          sortedData.map((item, index) => (
            <div key={index} className="popup-item px-3 pt-1 pb-2">
              <div className="history-row">
                <div className="username">
                  <div className="fs12 poppins-medium dark-text text-lowercase text-truncate">
                    {item.modified_by ? `${item.modified_by}:` : "—"}
                  </div>
                  <div className="fs12 cell-gray">
                    {formatDate(item.modified_at_CET)}
                  </div>
                </div>
                <div className="history-badges">
                  <StatusBadge type="updation" status={item.document_status} />
                  <span className="status-span fs11">
                    {translateStatus(t, item.status || "Updated")}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  const openHistory = () => {
    handleSelectedHistory?.(record.id);
    setHistoryOpen(true);
  };

  const handleHistoryOpenChange = (open) => {
    setHistoryOpen(open);
    if (!open) handleSelectedHistory?.(null);
  };

  const handleOnDownload = async () => {
    await handleDownload(record.sas_document_link);

    if (
      environment.APP_ENVIRONMENT !== "alabusTesting" &&
      environment.APP_ENVIRONMENT !== "eponaDevelopment" &&
      environment.APP_ENVIRONMENT !== "eponaTesting" &&
      environment.APP_ENVIRONMENT !== "eponaProduction"
    ) {
      // Disabling download excel for alabus client
      if (record.sas_excel_link) {
        setTimeout(async () => {
          await handleDownload(record.sas_excel_link);
        }, 3000);
      }
    }
  };

  const handleOpen = () => {
    if (isFailedStatus(record.extraction_status)) {
      if (onOpenFailure) onOpenFailure(record);
    } else {
      markDocumentOpened(record.id);
      dispatch(CommonAction.setSelectedDocument(record.id));
      dispatch(CommonAction.setCurrentPosition(saveQueueScrollTop()));
      navigate(`/invoice/${record.id}`);
    }
  };

  const confirmReprocess = () => {
    Modal.confirm({
      title: t("reprocessConfirmTitle"),
      content: t("reprocessConfirmContent"),
      okText: t("reprocess"),
      cancelText: t("cancel"),
      onOk: async () => {
        try {
          await documentService.reprocess(record.id);
          notification.success({ message: t("documentReprocessing") });
          if (onDataChanged) onDataChanged();
        } catch (err) {
          notification.error({
            message: t("reprocessError"),
            description: err?.message || t("genericError"),
          });
          throw err;
        }
      },
    });
  };

  const menuItems = [
    {
      key: "open",
      icon: <EditOutlined />,
      label: isFailedStatus(record.extraction_status)
        ? t("viewDetails")
        : t("edit"),
      onClick: handleOpen,
    },
    {
      key: "download",
      icon: <DownloadOutlined />,
      label: t("download"),
      onClick: handleOnDownload,
    },
    ...(!isFailedStatus(record.extraction_status)
      ? [
          {
            key: "reprocess",
            icon: <ReloadOutlined />,
            label: t("reprocess"),
            onClick: confirmReprocess,
          },
        ]
      : []),
    {
      key: "history",
      icon: <HistoryOutlined />,
      label: t("viewHistory"),
      onClick: openHistory,
    },
    {
      key: "notes",
      icon: <FileTextOutlined />,
      label: (
        <Tooltip
          title={latestNote ? latestNote.text : t("no_content")}
          placement="left"
        >
          <span>{t("notesLog")}</span>
        </Tooltip>
      ),
      onClick: () => setNotesOpen(true),
    },
  ];

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Popover
        open={historyOpen}
        onOpenChange={handleHistoryOpenChange}
        content={<HistoryPopup data={record.version_history} />}
        // Controlled only — opened from the menu item, not the ⋯ click.
        trigger={[]}
        overlayClassName="ant-popover-history"
        placement="topRight"
        arrow={{ pointAtCenter: true }}
        autoAdjustOverflow
        destroyTooltipOnHide
        getPopupContainer={() => document.body}
      >
        <span className="d-inline-flex">
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            placement="bottomRight"
            overlayClassName="app-dropdown"
          >
            <Badge count={notesCount} size="small" offset={[-6, 6]}>
              <Button
                type="text"
                icon={<MoreOutlined />}
                aria-label={t("action")}
                className={
                  selectedHistory === record.id || historyOpen
                    ? "text-blue-a"
                    : ""
                }
              />
            </Badge>
          </Dropdown>
        </span>
      </Popover>
      <NotesModal
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        documentId={record.id}
        notesLog={notesLog}
        legacyNote={currentLegacyNote}
        onSaved={handleNotesSaved}
      />
    </span>
  );
};

export default Actions;
